/**
 * Admin Standards Page
 *
 * Lists all official standards grouped by audit template.
 * Each template section shows its standards with active indicator.
 * Provides upload dialog, search, status filtering, and active/inactive filtering.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  ShieldCheck,
  Upload,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { api } from "convex/_generated/api";
import { Button, Input } from "@/components/ui/primitives";
import { StateCard } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { StandardCard } from "./StandardCard";
import { UploadStandardDialog } from "./UploadStandardDialog";
import type { FunctionReturnType } from "convex/server";

type OfficialStandard = FunctionReturnType<
  typeof api.admin_standards_queries.list
>[number];

type StandardStatus = OfficialStandard["status"];

const ALL_STATUSES: StandardStatus[] = [
  "uploading",
  "extracting",
  "extracted",
  "extraction_failed",
  "approved",
  "generating_checklist",
  "checklist_generation_failed",
  "published",
  "rejected",
];

type ActiveFilter = "all" | "active" | "inactive";

interface TemplateGroup {
  templateId: string | null;
  templateCode: string | null;
  templateDisplayName: string | null;
  standards: OfficialStandard[];
  activeStandard: OfficialStandard | null;
}

function groupByTemplate(standards: OfficialStandard[]): TemplateGroup[] {
  const groups = new Map<string, TemplateGroup>();

  for (const standard of standards) {
    const key = standard.templateId ?? "__no_template__";
    let group = groups.get(key);
    if (!group) {
      group = {
        templateId: standard.templateId ?? null,
        templateCode: standard.templateCode ?? null,
        templateDisplayName: standard.templateDisplayName ?? null,
        standards: [],
        activeStandard: null,
      };
      groups.set(key, group);
    }
    group.standards.push(standard);
    if (standard.isActive !== false && standard.status === "published") {
      group.activeStandard = standard;
    }
  }

  // Sort groups by template code alphabetically
  return Array.from(groups.values()).sort((a, b) => {
    const codeA = a.templateCode ?? "";
    const codeB = b.templateCode ?? "";
    return codeA.localeCompare(codeB);
  });
}

export function StandardsPage() {
  const { t } = useTranslation(["admin", "common"]);
  const { data: standards, isLoading } = useQuery(
    convexQuery(api.admin_standards_queries.list, {}),
  );

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<Set<StandardStatus>>(
    new Set(),
  );
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const filteredStandards = useMemo(() => {
    if (!standards) return [];

    return standards.filter((s) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch =
          (s.templateCode?.toLowerCase().includes(q) ?? false) ||
          (s.templateDisplayName?.toLowerCase().includes(q) ?? false) ||
          s.fileName.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      if (selectedStatuses.size > 0 && !selectedStatuses.has(s.status)) {
        return false;
      }

      const isActive = s.isActive !== false;
      if (activeFilter === "active" && !isActive) return false;
      if (activeFilter === "inactive" && isActive) return false;

      return true;
    });
  }, [standards, searchQuery, selectedStatuses, activeFilter]);

  const templateGroups = useMemo(
    () => groupByTemplate(filteredStandards),
    [filteredStandards],
  );

  const toggleStatus = (status: StandardStatus) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isEmpty = !isLoading && standards && standards.length === 0;
  const hasFilters =
    searchQuery || selectedStatuses.size > 0 || activeFilter !== "all";
  const noResults =
    !isLoading && !isEmpty && filteredStandards.length === 0 && hasFilters;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className="text-accent" />
          <h1 className="text-[18px] font-semibold text-primary">
            {t("admin:standards.title")}
          </h1>
        </div>
        <div className="flex-1" />
        <Button
          variant="primary"
          icon={Upload}
          onClick={() => setUploadDialogOpen(true)}
        >
          {t("admin:standards.uploadButton")}
        </Button>
      </div>

      {/* Search & Filters */}
      {!isLoading && !isEmpty && (
        <div className="space-y-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("admin:standards.search.placeholder")}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-primary cursor-pointer"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
              {(["all", "active", "inactive"] as const).map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  className={`rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors cursor-pointer ${
                    activeFilter === filter
                      ? "bg-accent/10 text-accent"
                      : "text-muted hover:text-primary"
                  }`}
                >
                  {t(
                    `admin:standards.search.show${filter.charAt(0).toUpperCase()}${filter.slice(1)}` as "admin:standards.search.showAll",
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 flex-wrap">
              {ALL_STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => toggleStatus(status)}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors cursor-pointer ${
                    selectedStatuses.has(status)
                      ? "bg-accent/15 text-accent ring-1 ring-accent/30"
                      : "bg-muted/10 text-muted hover:bg-muted/20"
                  }`}
                >
                  {t(`admin:standards.status.${status}`)}
                </button>
              ))}
              {selectedStatuses.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedStatuses(new Set())}
                  className="rounded-full px-2 py-0.5 text-[11px] text-muted hover:text-primary cursor-pointer"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="loading">
            <StateCard.Icon />
            <StateCard.Title>
              {t("admin:standards.loading.title")}
            </StateCard.Title>
            <StateCard.Description>
              {t("admin:standards.loading.description")}
            </StateCard.Description>
          </StateCard>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>
              {t("admin:standards.empty.title")}
            </StateCard.Title>
            <StateCard.Description>
              {t("admin:standards.empty.description")}
            </StateCard.Description>
            <StateCard.Action>
              <button
                type="button"
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
              >
                <Upload size={14} />
                {t("admin:standards.empty.uploadFirst")}
              </button>
            </StateCard.Action>
          </StateCard>
        </div>
      )}

      {/* No filter results */}
      {noResults && (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted">
            {t("admin:standards.search.noResults")}
          </p>
        </div>
      )}

      {/* Grouped standards list */}
      {!isLoading && !isEmpty && templateGroups.length > 0 && (
        <div className="space-y-4">
          {templateGroups.map((group) => {
            const groupKey = group.templateId ?? "__no_template__";
            const isCollapsed = collapsedGroups.has(groupKey);

            return (
              <div
                key={groupKey}
                className="rounded-xl border border-border bg-card/50 overflow-hidden"
              >
                {/* Template group header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-card-hover/30 transition-colors cursor-pointer"
                >
                  {isCollapsed ? (
                    <ChevronRight size={16} className="text-muted shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-muted shrink-0" />
                  )}

                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[14px] font-semibold text-primary">
                      {group.templateCode ??
                        t("admin:standards.group.noTemplate")}
                    </span>
                    {group.templateDisplayName && (
                      <span className="text-[13px] text-secondary truncate">
                        {group.templateDisplayName}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-auto shrink-0">
                    {/* Active standard indicator */}
                    {group.activeStandard ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-green-500/10 text-green-600">
                        <CheckCircle2 size={12} />
                        {group.activeStandard.fileName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-amber-500/10 text-amber-600">
                        {t("admin:standards.group.noActiveStandard")}
                      </span>
                    )}

                    {/* Count badge */}
                    <span className="inline-flex items-center justify-center rounded-full bg-muted/10 px-2 py-0.5 text-[11px] font-medium text-muted">
                      {group.standards.length}
                    </span>
                  </div>
                </button>

                {/* Standards in this group */}
                {!isCollapsed && (
                  <div className="px-3 pb-3 space-y-2">
                    {group.standards.map((standard) => (
                      <StandardCard key={standard._id} standard={standard} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <UploadStandardDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
    </div>
  );
}
